import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Checkbox,
    FormControlLabel,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    OutlinedInput,
    Paper,
} from '@mui/material';
import { Campaign, AdVariation } from '../models/Campaign';
import { CampaignService } from '../services/firebase';
import ImageUpload from './ImageUpload';
import VideoUpload from './VideoUpload';
import { VALID_CATEGORIES } from '../utils/standardizeCategories';
import { Delete as DeleteIcon } from '@mui/icons-material';

interface BulkAdVariationDialogProps {
    open: boolean;
    onClose: () => void;
    campaigns: Campaign[];
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const BulkAdVariationDialog: React.FC<BulkAdVariationDialogProps> = ({
    open,
    onClose,
    campaigns,
}) => {
    const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [variationData, setVariationData] = useState<Partial<AdVariation>>({
        name: '',
        start_time: '00:00',
        end_time: '23:59',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Filter campaigns based on selected category
    const filteredCampaigns = useMemo(() => {
        return campaigns.filter(campaign => 
            !selectedCategory || campaign.category === selectedCategory
        );
    }, [campaigns, selectedCategory]);

    const handleCategoryChange = (category: string) => {
        setSelectedCategory(category);
        // Clear previous selections when changing category
        setSelectedCampaignIds([]);
    };

    const handleCampaignSelectionChange = (event: any) => {
        const {
            target: { value },
        } = event;
        setSelectedCampaignIds(
            typeof value === 'string' ? value.split(',') : value,
        );
    };

    const handleSelectAll = () => {
        const allIds = filteredCampaigns
            .filter(campaign => campaign.id)
            .map(campaign => campaign.id!);
        setSelectedCampaignIds(allIds);
    };

    const handleClearSelection = () => {
        setSelectedCampaignIds([]);
    };

    const handleImageUpload = async (imageUrl: string) => {
        setVariationData(prev => ({
            ...prev,
            image_url: imageUrl,
            video_url: '',
            ad_type: 'image'
        }));
    };

    const handleVideoUpload = async (videoUrl: string) => {
        setVariationData(prev => ({
            ...prev,
            video_url: videoUrl,
            image_url: '',
            ad_type: 'video'
        }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            if (selectedCampaignIds.length === 0) {
                throw new Error('Please select at least one campaign');
            }

            if (!variationData.name) {
                throw new Error('Variation name is required');
            }

            if (!variationData.image_url && !variationData.video_url) {
                throw new Error('Please upload either an image or video');
            }

            // Create new variation object
            const newVariation: AdVariation = {
                id: Date.now().toString(),
                name: variationData.name || '',
                start_time: variationData.start_time || '00:00',
                end_time: variationData.end_time || '23:59',
                start_date: variationData.start_date,
                end_date: variationData.end_date,
                image_url: variationData.image_url || '',
                video_url: variationData.video_url || '',
                ad_type: variationData.ad_type || 'image',
            };

            // Add variation to each selected campaign
            await Promise.all(
                selectedCampaignIds.map(async (campaignId) => {
                    const campaign = campaigns.find(c => c.id === campaignId);
                    if (campaign) {
                        const updatedCampaign = {
                            ...campaign,
                            ad_variations: [...(campaign.ad_variations || []), newVariation]
                        };
                        await CampaignService.updateCampaign(campaignId, updatedCampaign);
                    }
                })
            );

            setSuccess(true);
            setTimeout(() => {
                onClose();
                // Reset form
                setSelectedCampaignIds([]);
                setVariationData({
                    name: '',
                    start_time: '00:00',
                    end_time: '23:59',
                    start_date: new Date().toISOString().split('T')[0],
                    end_date: new Date().toISOString().split('T')[0],
                });
            }, 1500);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Add Ad Variation to Multiple Campaigns</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Ad variation added successfully!
                    </Alert>
                )}

                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Filter and Select Campaigns
                    </Typography>
                    
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Category Filter</InputLabel>
                        <Select
                            value={selectedCategory}
                            label="Category Filter"
                            onChange={(e) => handleCategoryChange(e.target.value)}
                        >
                            <MenuItem value="">All Categories</MenuItem>
                            <MenuItem value={VALID_CATEGORIES.FRANCHISE}>{VALID_CATEGORIES.FRANCHISE}</MenuItem>
                            <MenuItem value={VALID_CATEGORIES.CORPORATE}>{VALID_CATEGORIES.CORPORATE}</MenuItem>
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={handleSelectAll}
                            fullWidth
                        >
                            Select All {selectedCategory || 'Campaigns'}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleClearSelection}
                            fullWidth
                        >
                            Clear Selection
                        </Button>
                    </Box>

                    <FormControl fullWidth>
                        <InputLabel>Select Campaigns</InputLabel>
                        <Select
                            multiple
                            value={selectedCampaignIds}
                            onChange={handleCampaignSelectionChange}
                            input={<OutlinedInput label="Select Campaigns" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((campaignId) => {
                                        const campaign = campaigns.find(c => c.id === campaignId);
                                        return campaign ? (
                                            <Chip 
                                                key={campaignId} 
                                                label={`${campaign.centre_name} (${campaign.city || 'No city'})`}
                                                size="small"
                                            />
                                        ) : null;
                                    })}
                                </Box>
                            )}
                            MenuProps={MenuProps}
                        >
                            {filteredCampaigns.map((campaign) => (
                                campaign.id && (
                                    <MenuItem key={campaign.id} value={campaign.id}>
                                        {`${campaign.centre_name} (${campaign.city || 'No city'}) - ${campaign.category || 'No category'}`}
                                    </MenuItem>
                                )
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Typography variant="h6" gutterBottom>
                    Ad Variation Details
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        fullWidth
                        label="Variation Name"
                        value={variationData.name}
                        onChange={(e) => setVariationData(prev => ({ ...prev, name: e.target.value }))}
                        required
                    />
                    
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Start Time"
                            type="time"
                            value={variationData.start_time}
                            onChange={(e) => setVariationData(prev => ({ ...prev, start_time: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            label="End Time"
                            type="time"
                            value={variationData.end_time}
                            onChange={(e) => setVariationData(prev => ({ ...prev, end_time: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Start Date"
                            type="date"
                            value={variationData.start_date}
                            onChange={(e) => setVariationData(prev => ({ ...prev, start_date: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            label="End Date"
                            type="date"
                            value={variationData.end_date}
                            onChange={(e) => setVariationData(prev => ({ ...prev, end_date: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <ImageUpload
                            campaignId="bulk"
                            onUploadComplete={handleImageUpload}
                        />
                        <Typography color="text.secondary" sx={{ mx: 2, alignSelf: 'center' }}>
                            or
                        </Typography>
                        <VideoUpload
                            campaignId="bulk"
                            onUploadComplete={handleVideoUpload}
                        />
                    </Box>

                    {/* Media Preview Section */}
                    {(variationData.image_url || variationData.video_url) && (
                        <Paper 
                            sx={{ 
                                p: 2, 
                                mt: 2, 
                                bgcolor: 'background.default',
                                position: 'relative'
                            }}
                        >
                            <Typography variant="subtitle1" gutterBottom>
                                Media Preview
                            </Typography>
                            <Box sx={{ 
                                position: 'relative',
                                width: '100%',
                                height: '200px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                overflow: 'hidden',
                                borderRadius: 1,
                                bgcolor: 'background.paper'
                            }}>
                                {variationData.image_url ? (
                                    <img
                                        src={variationData.image_url}
                                        alt="Ad variation preview"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain'
                                        }}
                                    />
                                ) : variationData.video_url ? (
                                    <video
                                        src={variationData.video_url}
                                        controls
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%'
                                        }}
                                    >
                                        Your browser does not support the video tag.
                                    </video>
                                ) : null}
                            </Box>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => setVariationData(prev => ({
                                    ...prev,
                                    image_url: '',
                                    video_url: '',
                                    ad_type: undefined
                                }))}
                                sx={{ mt: 1 }}
                            >
                                Remove Media
                            </Button>
                        </Paper>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                    {loading ? 'Adding...' : 'Add Variation'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkAdVariationDialog; 